const SITE_URL = 'https://www.grandpal.co';

interface TownNursingHome {
  center_name?: string | null;
  nh_slug: string;
  county_slug?: string | null;
  standardized_phone_number?: string | null;
  standardized_address?: string | null;
  town?: string | null;
  county?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  description?: string | null;
  short_description?: string | null;
}

/**
 * Generates a CollectionPage + ItemList of LocalBusiness (NursingHome) entries
 * for a town-level landing page — signals to Google exactly what the page is about.
 */
export function generateTownPageSchema({
  townName,
  countyName,
  countySlug,
  townSlug,
  nursingHomes,
  breadcrumbs,
}: {
  townName: string;
  countyName: string;
  countySlug: string;
  townSlug: string;
  nursingHomes: TownNursingHome[];
  breadcrumbs: { name: string; url: string }[];
}) {
  const pageUrl = `${SITE_URL}/nursing-homes/${countySlug}/town/${townSlug}`;

  // BreadcrumbList
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: `${SITE_URL}${crumb.url}`,
    })),
  };

  // ItemList of nursing homes — each as a MedicalBusiness (subtype of LocalBusiness)
  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Nursing Homes in ${townName}, Co. ${countyName}`,
    description: `A list of nursing homes located in ${townName}, County ${countyName}, Ireland, including HIQA-registered facilities.`,
    url: pageUrl,
    numberOfItems: nursingHomes.length,
    itemListElement: nursingHomes.map((home, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'MedicalBusiness',
        '@id': `${SITE_URL}/nursing-homes/${home.county_slug ?? countySlug}/${home.nh_slug}`,
        name: home.center_name ?? 'Nursing Home',
        url: `${SITE_URL}/nursing-homes/${home.county_slug ?? countySlug}/${home.nh_slug}`,
        ...(home.standardized_phone_number && { telephone: home.standardized_phone_number }),
        ...(home.description || home.short_description
          ? { description: home.short_description ?? home.description }
          : {}),
        address: {
          '@type': 'PostalAddress',
          addressLocality: home.town ?? townName,
          addressRegion: home.county ?? countyName,
          addressCountry: 'IE',
          ...(home.standardized_address && { streetAddress: home.standardized_address }),
        },
        ...(home.latitude && home.longitude
          ? {
              geo: {
                '@type': 'GeoCoordinates',
                latitude: home.latitude,
                longitude: home.longitude,
              },
            }
          : {}),
        areaServed: {
          '@type': 'City',
          name: townName,
          containedInPlace: {
            '@type': 'AdministrativeArea',
            name: `County ${countyName}`,
          },
        },
      },
    })),
  };

  return [breadcrumbSchema, itemListSchema];
}
